// src/pages/admin/AdminActivations.jsx
// git commit: "redesign(AdminActivations): stat cards, detail panel, filter pills, mobile sheet"
import { useState, useEffect, useCallback } from "react";
import adminApi from "../../api/adminApi";
import {
  RefreshCw,
  ChevronRight,
  X,
  Phone,
  Zap,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Hash,
  Server,
} from "lucide-react";
import { toast } from "react-toastify";

const STATUS_FILTERS = ["all", "pending", "otp_received", "cancelled", "expired"];

const STATUS_CONFIG = {
  pending: {
    color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    icon: Clock,
    dot: "bg-amber-500",
  },
  otp_received: {
    color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    icon: CheckCircle2,
    dot: "bg-green-500",
  },
  cancelled: {
    color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    icon: XCircle,
    dot: "bg-red-500",
  },
  expired: {
    color: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
    icon: AlertCircle,
    dot: "bg-gray-400",
  },
  cancel_requested: {
    color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
    icon: AlertCircle,
    dot: "bg-orange-500",
  },
};

export default function AdminActivations() {
  const [activations, setActivations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [cancelingId, setCancelingId] = useState(null);

  const fetchActivations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApi.get("/activations");
      setActivations(res.data.data || []);
    } catch {
      toast.error("Failed to fetch activations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivations();
  }, [fetchActivations]);

  // Keep selected in sync
  useEffect(() => {
    if (selected) {
      const updated = activations.find((a) => a._id === selected._id);
      if (updated) setSelected(updated);
    }
  }, [activations]);

  const handleManualCancel = async (id) => {
    if (!window.confirm("Cancel and refund this activation?")) return;
    setCancelingId(id);
    try {
      await adminApi.post(`/activations/${id}/cancel`);
      toast.success("Activation cancelled and refunded");
      fetchActivations();
      if (selected?._id === id) setSelected(null);
    } catch {
      toast.error("Failed to cancel activation");
    } finally {
      setCancelingId(null);
    }
  };

  // Stats
  const stats = {
    total: activations.length,
    pending: activations.filter((a) => a.status === "pending").length,
    success: activations.filter((a) => a.status === "otp_received").length,
    cancelled: activations.filter((a) =>
      ["cancelled", "expired"].includes(a.status)
    ).length,
  };

  // Filter + search
  const filtered = activations.filter((a) => {
    const matchFilter = filter === "all" || a.status === filter;
    const matchSearch =
      !search.trim() ||
      (a.activationId || "").toLowerCase().includes(search.toLowerCase()) ||
      (a.number || a.phone || "").includes(search) ||
      (a.service || "").toLowerCase().includes(search.toLowerCase()) ||
      (a.userId?.username || "").toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const getStatusConfig = (status) =>
    STATUS_CONFIG[status] || STATUS_CONFIG.expired;

  return (
    <div className="space-y-5 h-full">
      {/* ── Stat Cards ───────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          {
            label: "Total Activations",
            value: stats.total,
            icon: Zap,
            color: "text-blue-600 dark:text-blue-400",
            bg: "bg-blue-50 dark:bg-blue-900/20",
          },
          {
            label: "Pending",
            value: stats.pending,
            icon: Clock,
            color: "text-amber-600 dark:text-amber-400",
            bg: "bg-amber-50 dark:bg-amber-900/20",
          },
          {
            label: "OTP Received",
            value: stats.success,
            icon: CheckCircle2,
            color: "text-green-600 dark:text-green-400",
            bg: "bg-green-50 dark:bg-green-900/20",
          },
          {
            label: "Cancelled / Expired",
            value: stats.cancelled,
            icon: XCircle,
            color: "text-red-600 dark:text-red-400",
            bg: "bg-red-50 dark:bg-red-900/20",
          },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div
            key={label}
            className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4"
          >
            <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center mb-3`}>
              <Icon size={16} className={color} />
            </div>
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              {value.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* ── Main panel ───────────────────────────────────── */}
      <div className="flex gap-5 min-h-0">
        {/* Left: list */}
        <div
          className={`flex flex-col bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden transition-all duration-300 ${
            selected ? "w-full lg:w-3/5" : "w-full"
          }`}
        >
          {/* Toolbar */}
          <div className="p-4 border-b border-gray-100 dark:border-gray-800 space-y-3">
            {/* Status pills */}
            <div className="flex gap-2 flex-wrap">
              {STATUS_FILTERS.map((f) => {
                const cfg = getStatusConfig(f);
                return (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition capitalize ${
                      filter === f
                        ? "bg-purple-600 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                    }`}
                  >
                    {f === "all" ? "All" : f.replace("_", " ")}
                  </button>
                );
              })}
            </div>
            {/* Search + refresh */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Phone
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search ID, phone, service, username..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                />
              </div>
              <button
                onClick={fetchActivations}
                disabled={loading}
                className="p-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-400 transition"
              >
                <RefreshCw
                  size={15}
                  className={`text-gray-500 ${loading ? "animate-spin" : ""}`}
                />
              </button>
            </div>
          </div>

          {/* Count row */}
          <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Showing{" "}
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {filtered.length}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {activations.length}
              </span>{" "}
              activations
            </p>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <RefreshCw size={24} className="text-purple-500 animate-spin" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading activations...</p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex-1 flex items-center justify-center py-20">
              <div className="text-center">
                <Zap size={36} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No activations found
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto flex-1">
                <table className="w-full">
                  <thead className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
                    <tr>
                      {["Phone", "Service", "OTP", "Status", "Provider", ""].map((h) => (
                        <th
                          key={h}
                          className="px-5 py-3 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                    {filtered.map((act) => {
                      const cfg = getStatusConfig(act.status);
                      const StatusIcon = cfg.icon;
                      return (
                        <tr
                          key={act._id}
                          onClick={() =>
                            setSelected(selected?._id === act._id ? null : act)
                          }
                          className={`cursor-pointer transition-all ${
                            selected?._id === act._id
                              ? "bg-purple-50 dark:bg-purple-900/10"
                              : "hover:bg-gray-50 dark:hover:bg-gray-800/40"
                          }`}
                        >
                          <td className="px-5 py-3.5">
                            <p className="text-sm font-mono font-medium text-gray-900 dark:text-white">
                              {act.number || act.phone || "—"}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                              {act.activationId}
                            </p>
                          </td>
                          <td className="px-5 py-3.5 text-sm text-gray-700 dark:text-gray-300 capitalize">
                            {act.service}
                          </td>
                          <td className="px-5 py-3.5">
                            {act.otp ? (
                              <span className="font-mono text-sm font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded-lg">
                                {act.otp}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium capitalize ${cfg.color}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                              {act.status?.replace("_", " ")}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-gray-400 capitalize">
                            {act.provider || "—"}
                          </td>
                          <td className="px-5 py-3.5">
                            <ChevronRight
                              size={16}
                              className={`text-gray-300 transition-transform ${
                                selected?._id === act._id
                                  ? "rotate-90 text-purple-500"
                                  : ""
                              }`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-800 overflow-y-auto">
                {filtered.map((act) => {
                  const cfg = getStatusConfig(act.status);
                  return (
                    <button
                      key={act._id}
                      onClick={() =>
                        setSelected(selected?._id === act._id ? null : act)
                      }
                      className={`w-full text-left px-4 py-4 flex items-center gap-3 transition ${
                        selected?._id === act._id
                          ? "bg-purple-50 dark:bg-purple-900/10"
                          : "hover:bg-gray-50 dark:hover:bg-gray-800/40"
                      }`}
                    >
                      <div className="w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                        <Zap size={16} className="text-purple-600 dark:text-purple-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-mono font-medium text-gray-900 dark:text-white truncate">
                            {act.number || act.phone || "—"}
                          </p>
                          <span
                            className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium capitalize ${cfg.color}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                            {act.status?.replace("_", " ")}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 capitalize">
                          {act.service} • {act.provider}
                        </p>
                        {act.otp && (
                          <p className="text-xs font-mono font-bold text-purple-600 dark:text-purple-400 mt-0.5">
                            OTP: {act.otp}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Right: Detail panel — desktop */}
        {selected && (
          <div className="hidden lg:flex flex-col w-2/5 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Activation Details
              </h3>
              <button
                onClick={() => setSelected(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Phone + OTP hero */}
              <div className="bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-xl p-5 text-center">
                <p className="text-2xl font-mono font-bold text-purple-700 dark:text-purple-300">
                  {selected.number || selected.phone || "—"}
                </p>
                {selected.otp ? (
                  <div className="mt-2">
                    <p className="text-xs text-purple-500 dark:text-purple-400 mb-1">OTP Code</p>
                    <p className="text-xl font-mono font-bold text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/20 rounded-lg py-1.5 px-3 inline-block">
                      {selected.otp}
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-purple-400 dark:text-purple-500 mt-1">
                    No OTP received yet
                  </p>
                )}
              </div>

              {/* Status badge */}
              {(() => {
                const cfg = getStatusConfig(selected.status);
                const StatusIcon = cfg.icon;
                return (
                  <div className="flex justify-center">
                    <span
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium capitalize ${cfg.color}`}
                    >
                      <StatusIcon size={14} />
                      {selected.status?.replace("_", " ")}
                    </span>
                  </div>
                );
              })()}

              {/* Details grid */}
              <div className="space-y-0 border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                {[
                  {
                    icon: Hash,
                    label: "Activation ID",
                    value: selected.activationId || "—",
                    mono: true,
                  },
                  {
                    icon: Zap,
                    label: "Service",
                    value: selected.service || "—",
                    capitalize: true,
                  },
                  {
                    icon: Server,
                    label: "Provider",
                    value: selected.provider || "—",
                    capitalize: true,
                  },
                  {
                    icon: Clock,
                    label: "Created",
                    value: selected.createdAt
                      ? new Date(selected.createdAt).toLocaleString("en-NG", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—",
                  },
                ].map(({ icon: Icon, label, value, mono, capitalize }) => (
                  <div
                    key={label}
                    className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0"
                  >
                    <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                      <Icon size={13} className="text-gray-500 dark:text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-400 dark:text-gray-500">{label}</p>
                      <p
                        className={`text-sm font-medium text-gray-900 dark:text-white truncate ${
                          mono ? "font-mono" : capitalize ? "capitalize" : ""
                        }`}
                      >
                        {value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* User info */}
              {selected.userId && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <div className="w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 font-bold text-sm flex-shrink-0">
                    {selected.userId?.username?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {selected.userId?.username || "Unknown"}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {selected.userId?.email || "—"}
                    </p>
                  </div>
                </div>
              )}

              {/* Cancel action */}
              {selected.status === "pending" && (
                <button
                  onClick={() => handleManualCancel(selected._id)}
                  disabled={cancelingId === selected._id}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl text-sm font-medium transition"
                >
                  <XCircle size={16} />
                  {cancelingId === selected._id
                    ? "Cancelling..."
                    : "Cancel & Refund"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile detail sheet */}
      {selected && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          />
          <div className="relative w-full bg-white dark:bg-gray-900 rounded-t-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>

            <div className="px-5 pb-8 pt-3 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                  Activation Details
                </h3>
                <button
                  onClick={() => setSelected(null)}
                  className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <X size={18} className="text-gray-500" />
                </button>
              </div>

              {/* Phone + OTP */}
              <div className="bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-xl p-4 text-center">
                <p className="text-xl font-mono font-bold text-purple-700 dark:text-purple-300">
                  {selected.number || selected.phone || "—"}
                </p>
                {selected.otp && (
                  <div className="mt-2">
                    <p className="text-xs text-purple-500 mb-1">OTP</p>
                    <p className="text-lg font-mono font-bold text-green-700 dark:text-green-300">
                      {selected.otp}
                    </p>
                  </div>
                )}
              </div>

              {/* Status */}
              {(() => {
                const cfg = getStatusConfig(selected.status);
                const StatusIcon = cfg.icon;
                return (
                  <div className="flex justify-center">
                    <span
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium capitalize ${cfg.color}`}
                    >
                      <StatusIcon size={14} />
                      {selected.status?.replace("_", " ")}
                    </span>
                  </div>
                );
              })()}

              {/* Info rows */}
              <div className="space-y-2">
                {[
                  { label: "Activation ID", value: selected.activationId, mono: true },
                  { label: "Service", value: selected.service, capitalize: true },
                  { label: "Provider", value: selected.provider, capitalize: true },
                  {
                    label: "Created",
                    value: selected.createdAt
                      ? new Date(selected.createdAt).toLocaleString("en-NG", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—",
                  },
                ].map(({ label, value, mono, capitalize }) => (
                  <div
                    key={label}
                    className="flex justify-between items-start gap-3 py-2 border-b border-gray-100 dark:border-gray-800"
                  >
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {label}
                    </span>
                    <span
                      className={`text-xs font-medium text-gray-900 dark:text-white text-right ${
                        mono ? "font-mono" : capitalize ? "capitalize" : ""
                      }`}
                    >
                      {value || "—"}
                    </span>
                  </div>
                ))}
              </div>

              {/* Cancel */}
              {selected.status === "pending" && (
                <button
                  onClick={() => handleManualCancel(selected._id)}
                  disabled={cancelingId === selected._id}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-xl text-sm font-medium transition"
                >
                  <XCircle size={16} />
                  {cancelingId === selected._id ? "Cancelling..." : "Cancel & Refund"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
